import type { DocumentVersion } from "@prismicio/editor-fields"
import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { type ReactElement, Suspense, useEffect } from "react"
import { type Location, useLocation } from "react-router-dom"
import { z } from "zod"

import type { DocumentVersionStatus } from "../app/services/core"
import {
  usePrefetchStaticCustomType,
  useStaticCustomType,
} from "../app/services/customTypes"
import { getReleaseId } from "../app/services/release"
import { useTeamSpaceByCustomTypeId } from "../app/services/role"
import { CollaborationRoom } from "./collaboration/CollaborationRoom"
import { Document } from "./Document"
import {
  type ExistingDocumentData,
  preloadDocument,
  useDocument,
  useDocumentMeta,
  useDocumentMetaTitle,
} from "./documentData"
import { DocumentNotFoundErrorBoundary } from "./DocumentNotFoundErrorBoundary"
import { DocumentProvider } from "./DocumentProvider"
import { useExistingDocumentParams, useGoToDocument } from "./documentRouting"
import { DocumentSkeleton } from "./DocumentSkeleton"
import { findDocumentVersion } from "./findDocumentVersion"
import { LabPersonalizationFeedback } from "./LabPersonalizationFeedback"

export function ExistingDocument() {
  // We use the documentId as a key for the whole tree.
  // It re-creates the Suspense Boundary only for a big change: When the whole documentId changed (e.g, when going to a different locale).
  // If it's just the versionId that changed (e.g, clicking on the version panel), we have a different, more subtle transition.
  // We have to re-create the Boundary because all react-router changes occur within a startTransition()
  // which doesn't show any fallback if the Suspense boundaries in the new tree are still the same ones.
  const { documentId } = useExistingDocumentParams()

  return (
    <DocumentNotFoundErrorBoundary>
      <Suspense fallback={<DocumentSkeleton screen="existing" />}>
        <DataLoader>
          {(data) => (
            <DocumentProvider initData={data}>
              <CollaborationRoom key={documentId}>
                <Document />
                <LabPersonalizationFeedback />
              </CollaborationRoom>
            </DocumentProvider>
          )}
        </DataLoader>
      </Suspense>
    </DocumentNotFoundErrorBoundary>
  )
}

interface DataLoaderProps {
  children: (data: ExistingDocumentData) => ReactElement
}

function DataLoader(props: DataLoaderProps) {
  const { children } = props

  const { documentId, releaseId, status, archivedVersionId } =
    useExistingDocumentParams()

  // Step 1: Load the document meta; it's always the entry point.
  const meta = useDocumentMeta(documentId)

  // Step 2: Synchronously find our version in the meta.
  const version = findDocumentVersion({
    meta,
    documentId,
    releaseId,
    status,
    archivedVersionId,
  })

  // Step 3: Prefetch document data in parallel.
  usePrefetchStaticCustomType(meta.custom_type_id)
  preloadDocument(version.version_id)

  const location = useLocation()
  const initialDraft = maybeGetInitialDraft(location)
  const customType = useStaticCustomType(meta.custom_type_id).data
  const document = useDocument(version.version_id, customType)
  const teamSpace = useTeamSpaceByCustomTypeId(customType.id)

  const documentData: ExistingDocumentData = {
    type: "existing",
    document: initialDraft ?? document,
    isFromInitialDraft: Boolean(initialDraft),
    meta,
    version,
    customType,
    teamSpace,
  }

  useDocumentMetaTitle(documentData)

  const willRedirect = useRedirectToRecentVersion({
    documentId: meta.id,
    recentVersion: version,
    status: version.status,
    releaseId,
    archivedVersionId,
  })

  // No point rendering the wrong existingDocument only to fix that soon after.
  if (willRedirect) return null

  // 4) After any version redirects took place, initialize the collaboration room.
  // Suspense-block at this step because we want to seamlessly display the room's data OR the prismic data and show the same suspense fallback while we decide.
  // TODO: await for the room storage to be ready before rendering the document.
  // This is a temporary fix for the RTC support.
  // const room = initRoom?.(documentData)
  // if (room) use(room.waitUntilStorageReady())

  return children(documentData)
}

/*
 * useDocumentVersion tries to return the version data for the versionId we give it.
 * Failing that, it returns its most recent, known version, which happens when
 * using old URLs with a version that is no longer a part of the set of interesting versions.
 * This improves UX as instead of showing 404s all the time, we transparently redirect to that recent version instead.
 */
function useRedirectToRecentVersion(params: {
  documentId: string
  recentVersion: DocumentVersion
  status: DocumentVersionStatus
  releaseId: string | undefined
  archivedVersionId: string | undefined
}) {
  const { documentId, status, recentVersion, releaseId, archivedVersionId } =
    params

  const goToDocument = useGoToDocument()

  const recentVersionReleaseId = getReleaseId(recentVersion)

  const willRedirectArchiveVersion =
    recentVersion.status === "archived" &&
    archivedVersionId !== recentVersion.version_id

  const willRedirect =
    status !== recentVersion.status ||
    releaseId !== recentVersionReleaseId ||
    willRedirectArchiveVersion

  useEffect(() => {
    if (!willRedirect) return

    goToDocument({
      id: documentId,
      status: recentVersion.status,
      releaseId: recentVersionReleaseId,
      archivedVersionId:
        recentVersion.status === "archived"
          ? recentVersion.version_id
          : undefined,
      replace: true,
    })
  }, [
    archivedVersionId,
    documentId,
    goToDocument,
    recentVersion,
    recentVersionReleaseId,
    releaseId,
    willRedirect,
  ])

  return willRedirect
}

function maybeGetInitialDraft(location: Location) {
  const parsed = locationType.safeParse(location)
  if (!parsed.success) return

  return validateDocument(parsed.data.state.initialDraft)
}

function validateDocument(maybeDoc: object) {
  const result = DocumentT.decode(maybeDoc)

  if (result._tag === "Left") {
    console.warn("could not decode document")
    return {}
  }

  return result.right
}

const locationType = z.object({
  state: z.object({
    initialDraft: z.record(z.unknown()),
  }),
})
